import React from 'react';
import Button, { ButtonProps } from '@mui/material/Button';
import { Theme } from '@mui/material/styles';

export type MDButtonRootProps = ButtonProps & {
  theme: Theme;
  ownerState: {
    color: string;
    variant: string;
    size: string;
    circular: boolean;
    iconOnly: boolean;
    // darkMode: boolean;
  };
};

const MDButtonRoot = React.forwardRef<HTMLButtonElement, MDButtonRootProps>(
  ({ theme, ownerState, ...rest }, ref) => {
    const { palette, functions, borders, boxShadows } = theme as any;
    const { color, variant, size, circular, iconOnly } = ownerState;
    const darkMode = false; // ownerState.darkMode;

    const { white, text, transparent, gradients, grey } = palette;
    const { boxShadow, linearGradient, pxToRem, rgba } = functions;
    const { borderRadius } = borders;
    const { colored } = boxShadows;

    const containedStyles = () => {
      const backgroundValue = palette[color] ? palette[color].main : white.main;
      const focusedBackgroundValue = palette[color]
        ? palette[color].focus
        : white.focus;
      const boxShadowValue = colored[color]
        ? `${boxShadow([0, 3], [3, 0], palette[color].main, 0.15)}, ${boxShadow(
            [0, 3],
            [1, -2],
            palette[color].main,
            0.2
          )}, ${boxShadow([0, 1], [5, 0], palette[color].main, 0.15)}`
        : 'none';
      const hoveredBoxShadowValue = colored[color]
        ? `${boxShadow(
            [0, 14],
            [26, -12],
            palette[color].main,
            0.4
          )}, ${boxShadow(
            [0, 4],
            [23, 0],
            palette[color].main,
            0.15
          )}, ${boxShadow([0, 8], [10, -5], palette[color].main, 0.2)}`
        : 'none';
      let colorValue = white.main;

      if (
        !darkMode &&
        (color === 'white' || color === 'light' || !palette[color])
      ) {
        colorValue = text.main;
      } else if (
        darkMode &&
        (color === 'white' || color === 'light' || !palette[color])
      ) {
        colorValue = grey[600];
      }

      let focusedColorValue = white.main;

      if (color === 'white') {
        focusedColorValue = text.main;
      } else if (color === 'primary' || color === 'error' || color === 'dark') {
        focusedColorValue = white.main;
      }

      return {
        background: backgroundValue,
        color: colorValue,
        boxShadow: boxShadowValue,
        '&:hover': {
          backgroundColor: backgroundValue,
          boxShadow: hoveredBoxShadowValue,
        },
        '&:focus:not(:hover)': {
          backgroundColor: focusedBackgroundValue,
          boxShadow: palette[color]
            ? boxShadow([0, 0], [0, 3.2], palette[color].main, 0.5)
            : boxShadow([0, 0], [0, 3.2], white.main, 0.5),
        },
        '&:disabled': {
          backgroundColor: backgroundValue,
          color: focusedColorValue,
        },
      };
    };

    const outliedStyles = () => {
      const backgroundValue =
        color === 'white' ? rgba(white.main, 0.1) : transparent.main;
      const colorValue = palette[color] ? palette[color].main : white.main;
      const boxShadowValue = palette[color]
        ? boxShadow([0, 0], [0, 3.2], palette[color].main, 0.5)
        : boxShadow([0, 0], [0, 3.2], white.main, 0.5);
      let borderColorValue = palette[color]
        ? palette[color].main
        : rgba(white.main, 0.75);

      if (color === 'white') {
        borderColorValue = rgba(white.main, 0.75);
      }

      return {
        background: backgroundValue,
        color: colorValue,
        borderColor: borderColorValue,
        '&:hover': {
          background: transparent.main,
          borderColor: colorValue,
        },
        '&:focus:not(:hover)': {
          background: transparent.main,
          boxShadow: boxShadowValue,
        },
        '&:active:not(:hover)': {
          backgroundColor: colorValue,
          color: white.main,
          opacity: 0.85,
        },
        '&:disabled': {
          color: colorValue,
          borderColor: colorValue,
        },
      };
    };

    const gradientStyles = () => {
      const backgroundValue =
        color === 'white' || !gradients[color]
          ? white.main
          : linearGradient(gradients[color].main, gradients[color].state);
      const boxShadowValue = colored[color]
        ? `${boxShadow([0, 3], [3, 0], palette[color].main, 0.15)}, ${boxShadow(
            [0, 3],
            [1, -2],
            palette[color].main,
            0.2
          )}, ${boxShadow([0, 1], [5, 0], palette[color].main, 0.15)}`
        : 'none';
      const hoveredBoxShadowValue = colored[color]
        ? `${boxShadow(
            [0, 14],
            [26, -12],
            palette[color].main,
            0.4
          )}, ${boxShadow(
            [0, 4],
            [23, 0],
            palette[color].main,
            0.15
          )}, ${boxShadow([0, 8], [10, -5], palette[color].main, 0.2)}`
        : 'none';
      let colorValue = white.main;

      if (color === 'white') {
        colorValue = text.main;
      } else if (color === 'light') {
        colorValue = gradients.dark.state;
      }

      return {
        background: backgroundValue,
        color: colorValue,
        boxShadow: boxShadowValue,
        '&:hover': {
          boxShadow: hoveredBoxShadowValue,
        },
        '&:focus:not(:hover)': {
          boxShadow: boxShadowValue,
        },
        '&:disabled': {
          background: backgroundValue,
          color: colorValue,
        },
      };
    };

    const textStyles = () => {
      const colorValue = palette[color] ? palette[color].main : white.main;
      const focusedColorValue = palette[color]
        ? palette[color].focus
        : white.focus;

      return {
        color: colorValue,
        '&:hover': {
          color: focusedColorValue,
        },
        '&:focus:not(:hover)': {
          color: focusedColorValue,
        },
      };
    };

    const circularStyles = () => ({
      borderRadius: borderRadius.section,
    });

    const iconOnlyStyles = () => {
      let sizeValue = pxToRem(38);

      if (size === 'small') {
        sizeValue = pxToRem(25.4);
      } else if (size === 'large') {
        sizeValue = pxToRem(52);
      }

      let paddingValue = `${pxToRem(11)} ${pxToRem(11)} ${pxToRem(10)}`;

      if (size === 'small') {
        paddingValue = pxToRem(4.5);
      } else if (size === 'large') {
        paddingValue = pxToRem(16);
      }

      return {
        width: sizeValue,
        minWidth: sizeValue,
        height: sizeValue,
        minHeight: sizeValue,
        padding: paddingValue,
        '& .material-icons': {
          marginTop: 0,
        },
        '&:hover, &:focus, &:active': {
          transform: 'none',
        },
      };
    };

    const styles = {
      ...(variant === 'contained' && containedStyles()),
      ...(variant === 'outlined' && outliedStyles()),
      ...(variant === 'gradient' && gradientStyles()),
      ...(variant === 'text' && textStyles()),
      ...(circular && circularStyles()),
      ...(iconOnly && iconOnlyStyles()),
      // Flat, as the steerage app's buttons: the template's coloured glow
      // shadows are dropped, and gradients fill as one solid colour.
      boxShadow: 'none',
      '&:hover, &:focus, &:active, &:focus:not(:hover)': { boxShadow: 'none' },
      ...(variant === 'gradient' &&
        palette[color] && {
          background: `${palette[color].main} !important`,
          '&:hover': { background: `${palette[color].focus} !important`, boxShadow: 'none' },
        }),
    };

    return (
      <Button ref={ref} sx={styles} {...rest}>
        {rest.children}
      </Button>
    );
  }
);

MDButtonRoot.displayName = 'MDButtonRoot';

export default MDButtonRoot;
